import {
  DEFAULT_LEVEL,
  analyzeSession,
  combNotchFrequencies,
  generateDrill,
  getProfile,
  getThaalam,
  gradePitch,
  noteToFrequency,
  pitchVelocityCents,
  smoothPitchHz,
  SWARA_NAMES,
  TRANSITION_VELOCITY_CPS,
} from '@swarasense/shared';
import type {
  InstrumentId,
  NotationTimeline,
  PitchBand,
  PracticeLevel,
  RagaRecord,
  RecordedSample,
  SwaraSymbol,
  Thaalam,
} from '@swarasense/shared';
import type { PitchSample } from '../audio/engine.js';
import { AudioEngine } from '../audio/engine.js';
import { TanpuraSynth } from '../audio/tanpura.js';
import { Metronome } from '../audio/metronome.js';
import { createTonePlayer } from '../audio/tone.js';
import { allRagaRecords, loadSeedBundle, ragaSeedFor } from '../api/seed.js';
import { resolveRagaParam } from '../api/deep-link.js';
import { loadProfile, subscribeSa, updateProfile } from '../api/profile.js';
import { recordSession } from '../api/practice-history.js';
import { sessionFromReport } from '../api/personalization.js';
import { celebrate } from './celebrate.js';
import { renderEarTraining } from './ear-training.js';
import type { SingBackResult } from './ear-training.js';
import { NotationLane, swaraLabel } from './notation-lane.js';
import { clampedBeat, elapsedBeats, phraseComplete, renderPracticeReview } from './practice.js';
import { renderRagaRail } from './raga-rail.js';
import { mountHomeStrip } from './home-strip.js';
import { setBusyProbe } from './activity.js';
import { Ribbon } from './ribbon.js';
import { renderResultsPanel } from './results-panel.js';
import type { SessionRecording } from './results.js';
import { stopPlayback } from './results.js';
import { resolveScoring, saHzFor } from './scoring.js';
import type { Scoring } from './scoring.js';
import { createStore, type SessionState } from './store.js';
import { mountPopovers } from './settings-popover.js';
import { mountControlsRow } from './controls-row.js';
import { mountNavbar } from './navbar.js';

/**
 * Task 2 (targeted drills) dev/test trigger ONLY — never set outside the
 * Playwright screenshot harness or manual devtools testing, exactly like
 * `window.__SWARASENSE_TEST__`/`__feedPitch` (see `audio/engine.ts`'s
 * identical convention). Task 4 replaces this with the real "Try a warm-up
 * for {swara}" launch from the child Summary's nudge; until that lands, this
 * is the only way to reach practice mode, so the flow can be exercised and
 * screenshotted. Dead code in production — no production caller ever sets
 * `window.__SWARASENSE_TEST__`.
 */
declare global {
  interface Window {
    __SWARASENSE_ENTER_DRILL__?: (targetSwara?: string) => void;
    /** Ear-training plan Task 3's own dev/test trigger, same convention and
     * same rationale as `__SWARASENSE_ENTER_DRILL__` above: ear-training mode
     * has no other reachable entry point for the Playwright screenshot
     * harness to drive headlessly (the real "Ear training" button needs a
     * loaded raga + a user gesture chain the harness can still send, but this
     * skips straight to it for a deterministic capture). Dead code in
     * production. */
    __SWARASENSE_ENTER_EAR__?: () => void;
  }
}

/** Below this clarity, a detected pitch is a marginal/near-threshold read — the
 * ribbon greys it out (not scored) rather than banding it as a real note. See
 * the identical constant (and its rationale) in the pre-Phase-4b live screen. */
const CONFIDENCE_THRESHOLD = 0.88;

/** Sustained-wrong frames before the ribbon commits to the "wrong" band, so a
 * brief gamaka dip below/above the scale isn't alarmed as an error. */
const OFF_STREAK_TO_WRONG = 3;

/** Encouraging-feedback Task 4: how long a steady excellent-band note has to
 * hold before the ✨ in-tune moment fires — long enough that it rewards a
 * genuinely sustained note, not a passing dip through the band. */
const EXCELLENT_SUSTAIN_MS = 1000;

/** Ear-training Task 5 (OPTIONAL sing-back): how long a single "sing it"
 * mic listen runs before giving up and reading as "not-yet" — long enough
 * for a child to take a breath and sing one note, short enough that a
 * silent/unsung attempt doesn't leave the mic open and the button disabled
 * for an uncomfortable stretch. */
const SING_BACK_LISTEN_MS = 3000;
/** How long a sustained excellent/good-band read has to hold before a
 * sing-back listen resolves "landed" — shorter than free-practice's own
 * `EXCELLENT_SUSTAIN_MS` (a quick confirmation, not the live ribbon's
 * stricter "hold it" bar), but still long enough to reject a single noisy
 * frame skating past the target on its way to/from some other pitch. */
const SING_BACK_SUSTAIN_MS = 350;

/** Phase A voice-range finder: how long a single "sing your lowest/highest"
 * capture listens before giving up — generous, since finding + holding an
 * extreme comfortable note takes longer than echoing back a played tone. On
 * timeout it still resolves from whatever it heard (see `captureSustainedHz`'s
 * fallback), rather than failing outright, as long as it heard a real note. */
const RANGE_CAPTURE_LISTEN_MS = 7000;
/** How long a pitch must hold steady-ish before the range finder accepts it —
 * long enough that a passing glide isn't captured, short enough that an
 * untrained singer on a laptop mic can reach it. */
const RANGE_CAPTURE_HOLD_MS = 650;
/** Max deviation (cents, from the window median) for a frame to count toward
 * the "steady" cluster — wide enough to tolerate real vocal wobble/vibrato and
 * a range finder only needs the note's ballpark, not pinpoint tuning. */
const RANGE_CAPTURE_STABLE_CENTS = 90;
/** Minimum detector clarity for a frame to count as a voiced note during range
 * capture — deliberately LOWER than free practice's `CONFIDENCE_THRESHOLD`
 * (0.88): the finder only needs a rough pitch, and an unaccompanied held note
 * on a laptop mic rarely clears 0.88 every frame (which is why the first cut,
 * reusing 0.88, timed out on real mics). */
const RANGE_CAPTURE_MIN_CLARITY = 0.5;
/** Minimum voiced frames in the trailing window (or in total, for the timeout
 * fallback) before the finder trusts a pitch — rejects a lone spurious blip. */
const RANGE_CAPTURE_MIN_SAMPLES = 5;

/** Raga name + arohana/avarohana come from our own seed data (never user
 * input), so plain interpolation into the caption's innerHTML is fine — same
 * trust boundary the pre-Phase-4b setup screen used for this data. */
function stageCaptionHtml(record: RagaRecord): string {
  const parent =
    record.type === 'melakarta'
      ? `Melakarta #${record.melakarta_number}`
      : record.parent_melakarta !== null
        ? `Janya of Melakarta #${record.parent_melakarta}`
        : 'Janya';
  return `<b>${record.name}</b> · ${parent} · Ārohaṇa ${record.arohana.join(' ')} · Avarohaṇa ${record.avarohana.join(' ')}`;
}

function mmss(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Task 5 polish: `NotationTimeline.thaalam` is a free-form string (not
 * statically bound to `THAALAMS`, unlike the transport's own thaalam
 * `<select>` — see `settings-popover.ts`'s `getThaalam(thaalamName)!`, which
 * IS safe because that value only ever comes from the bounded dropdown). A
 * drill's timeline is built from `state.thaalamName` too, so this is always
 * valid today — but nothing enforces that at the type level, so a future
 * source of `NotationTimeline` (a song import, a corrupted/typo'd name)
 * could otherwise crash `metronome.start` mid-take on an unguarded `!`.
 * Falls back to Adi (always registered — see `thaalams.ts`'s `THAALAMS`)
 * rather than throwing, so an unknown thaalam degrades to a familiar beat
 * pattern instead of breaking the drill.
 */
function resolveThaalamOrAdi(name: string): Thaalam {
  return getThaalam(name) ?? getThaalam('Adi')!;
}

/**
 * The single-page app shell (Phase 4b redesign, Task 1): builds the shared,
 * once-only deps (audio engines, the session store, the seed load) and
 * renders the whole page directly into `#app` — the persistent navbar, the
 * slim controls row beneath it, the ribbon "stage" (raga caption, REC
 * indicator, the pitch ribbon, the current-swara readout + transport), and
 * an empty `#results` container Task 3 fills in after a take.
 *
 * UI reimagine (Task 2, shared navbar): the pre-reimagine single `.bar`
 * (wordmark + ragam combobox + Sa + Settings + theme) split in two — a
 * persistent `.navbar` (`navbar.ts`'s `mountNavbar`: wordmark + the four
 * Practice/Ragas/Ear training/Learn links + theme toggle, the SAME shared
 * markup `scripts/gen-content/skeleton.ts`'s `renderHeader()` emits for the
 * static content pages) sits at the very top, and a slim `.controls-row`
 * (`controls-row.ts`'s `mountControlsRow`, renamed from the old `topbar.ts`)
 * directly beneath it holds the ragam combobox, Sa, Tanpura, and Settings —
 * exactly the approved mockup's practice-page control strip. Both are
 * mounted inside `.app` (see the template below), so `settings-popover.ts`'s
 * `trigger.closest('.app')` walk (used to compute what to `inert` while a
 * popover is open) is unaffected by the split.
 *
 * Frontend-redesign-v2: the four option panels (Settings, Sa, Tanpura,
 * Thaalam) are no longer nested inside `.controls-row`/`.tgroup` as anchored
 * popovers — they, plus the shared scrim, now live in `.popover-layer`, a
 * SIBLING of `.app` mounted directly under `root`. That makes them true
 * top-level `position: fixed` children of the viewport: no ancestor of
 * `.popover-layer` ever applies a `transform`/`filter`/`backdrop-filter`
 * (the property that would otherwise make that ancestor the fixed-
 * positioning containing block instead of the viewport), so the panels can
 * never clip against a card edge or the ribbon the way an anchored popover
 * could. The trigger buttons themselves stay exactly where they were (gear/
 * Sa/Tanpura in the controls row, "⋯" beside Tanpura/Thaalam) — only the
 * panel's OWN element moved; `settings-popover.ts`'s accessible-dialog
 * mechanics (focus trap, Escape, scrim, inert background) are keyed off
 * element references, not DOM position, so none of that changed.
 *
 * There is no router any more: this page never navigates away from itself,
 * so every listener wired here lives for the app's whole lifetime and never
 * needs to unmount. The navbar's Ragas/Ear training/Learn links (and its
 * Practice link, which just points back at `/`) are real anchors — clicking
 * one is a normal, full-page navigation away from (or, for Practice, back
 * to) this SPA, not a client-side route change.
 */
export function mountApp(root: HTMLElement): void {
  const initial: SessionState = {
    pitchClass: 'G',
    octave: 3,
    ragaName: 'Mohanam',
    thaalamName: 'Adi',
    tempo: 80,
    instrument: 'vocal' as InstrumentId,
    level: DEFAULT_LEVEL as PracticeLevel,
    droneMode: 'inapp',
    droneVolume: 0.12,
    thaalamVolume: 0.6,
    micGain: 1.5,
    practiceMode: 'free',
    recSamples: [],
  };
  const store = createStore(initial);

  // Phase A (personalization): apply the saved on-device profile as this
  // visit's defaults, so a returning learner starts at their recommended Sa
  // and self-reported level instead of the built-in G3/beginner. Done here,
  // BEFORE `mountPopovers`/the ribbon/`store.subscribe` are wired below, so
  // every initial render (the transport's Sa label, scoring, the settings
  // panel) already reflects the profile — and because `subscribe` never
  // fires on registration (only on a later `set`), this early `set` writes
  // nothing back to the profile. Raga is intentionally NOT touched: a
  // `?raga=` deep-link owns that (resolved in `loadSeedBundle().then` below),
  // and Sa/level are independent of which raga is loaded.
  const savedProfile = loadProfile();
  if (savedProfile.sa) {
    store.set({ pitchClass: savedProfile.sa.pitchClass, octave: savedProfile.sa.octave });
  }
  if (savedProfile.level) store.set({ level: savedProfile.level });

  // Connected-Swarasense (W1): the shruti is shared site-wide via `profile.sa`.
  // A `storage` event fires when ANOTHER tab/page changes it (e.g. the Sa
  // control on a raga or tanpura page) — reflect that here so an open practice
  // tab stays in sync. Same-document changes already persist via the store
  // subscription below; this only handles cross-document updates.
  subscribeSa((sa) => {
    const s = store.get();
    if (sa.pitchClass !== s.pitchClass || sa.octave !== s.octave) {
      store.set({ pitchClass: sa.pitchClass, octave: sa.octave, calibration: undefined });
    }
  });

  // Task 5 (content SEO pages) + Task 6 (ear-training plan): the deep-link
  // params the static content pages' CTAs can send a visitor in with —
  // `?raga=<name>` (raga pages, `raga-page.ts`'s `ctaHref`), `?tanpura=1`
  // (the /tanpura/ page and the find-your-shruti guide), and `?mode=ear`
  // (the /ear-training/ page's CTA — read inside the `loadSeedBundle().then`
  // below, right after the `?raga=` override). Read once, up front:
  // `location.search` never changes over this page's lifetime (there's no
  // router — see this function's own doc comment above), so there's nothing
  // to gain from re-reading it at each use site below.
  const deepLinkParams = new URLSearchParams(location.search);

  const engine = new AudioEngine();
  // Connected-Swarasense (W8): the auto-updater holds a reload until the mic is
  // idle — a running engine means a take/drill/sing-back/range-finder is live.
  setBusyProbe(() => engine.running);
  const tanpura = new TanpuraSynth();
  const metronome = new Metronome();
  const tonePlayer = createTonePlayer();

  root.innerHTML = `
    <div class="app">
      <div class="controls-row" id="controls-row"></div>
      <div class="practice-grid" id="practice-grid">
        <section class="stage" aria-label="Practice stage">
          <div class="stage-head">
            <span class="raga-meta" id="raga-meta" aria-live="polite"></span>
            <span class="rec" id="rec" hidden><span class="dot"></span>REC <span id="rec-time">0:00</span></span>
          </div>
          <div class="practice-banner" id="practice-banner" hidden>
            <div class="practice-banner-text">
              <span class="practice-label" id="practice-label"></span>
              <p class="practice-pattern" id="practice-pattern"></p>
              <p class="practice-hint">Follow the cursor and sing along.</p>
            </div>
            <button class="btn" id="practice-exit-btn" type="button">Exit practice</button>
          </div>
          <div class="notation-lane-mount" id="notation-lane-mount" hidden></div>
          <div class="ribbon" id="ribbon"></div>
          <div class="live-row" id="live-row">
            <div class="now" id="now" data-state="idle">
              <span class="sw" id="cur-swara">—</span>
              <span class="verdict"><span class="v" id="cur-verdict">ready</span></span>
            </div>
            <div class="transport">
              <div class="anchor tgroup">
                <button class="btn" id="thaalam-btn" type="button" aria-pressed="false"><span class="g"></span>Thaalam</button>
                <button class="btn caret" id="thaalam-caret" type="button" aria-label="Thaalam options" aria-controls="thaalam-popover">⋯</button>
              </div>
              <button class="btn" id="ear-btn" type="button">🎧 Ear training</button>
              <button class="btn primary" id="listen-btn" type="button" aria-pressed="false">Start listening</button>
            </div>
          </div>
          <div class="ear-training-mount" id="ear-training-mount" hidden></div>
        </section>
        <aside class="raga-rail" id="raga-rail" aria-label="About this raga"></aside>
      </div>
      <section class="results" id="results" aria-live="polite"></section>
    </div>
    <div class="popover-layer" id="popover-layer">
      <div class="popover-scrim" id="popover-scrim" hidden></div>
      <div class="popover" id="sa-popover" role="dialog" aria-modal="true" aria-labelledby="sa-popover-title" hidden></div>
      <div class="popover" id="settings-popover" role="dialog" aria-modal="true" aria-labelledby="settings-popover-title" hidden></div>
      <div class="popover" id="drone-popover" role="dialog" aria-modal="true" aria-labelledby="drone-popover-title" hidden></div>
      <div class="popover" id="thaalam-popover" role="dialog" aria-modal="true" aria-labelledby="thaalam-popover-title" hidden></div>
    </div>`;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  // Document-scoped lookup for the shell elements that live OUTSIDE `#app` in
  // index.html (the persistent navbar, the landing hero, the home strip).
  // Guarded (`| null`): this `mountApp` also runs in harness/unit setups that
  // mount `#app` without the static shell markup.
  const $doc = <T extends HTMLElement>(id: string): T | null => document.getElementById(id) as T | null;

  const heroEl = $doc<HTMLElement>('hero-collapsible');

  /**
   * Homepage rework: the landing (hero) and the practice tool (`#app`) are two
   * VIEWS toggled by `<html data-view>` — the persistent navbar + footer show
   * in both. `"landing"` (default, every visit) shows the hero + welcome strip
   * and hides `#app`; `"practice"` shows `#app` and hides the hero. This
   * replaced the old collapse-on-engage model (which hid the landing for
   * returning visitors and stacked the tool below it — the redundancy the
   * homepage feedback called out). `inert` on the hidden hero keeps it out of
   * the tab order/a11y tree even though CSS also `display:none`s it.
   */
  function setView(view: 'landing' | 'practice'): void {
    document.documentElement.setAttribute('data-view', view);
    if (heroEl) heroEl.inert = view === 'practice';
  }
  // Catch up to whatever the no-FOUC inline script decided (practice for a
  // deep-link, else landing). Also sets `inert`, which that script can't.
  setView(document.documentElement.getAttribute('data-view') === 'practice' ? 'practice' : 'landing');

  /** Enter the practice view (from "Start practising", a deep-link, Continue,
   * or the navbar's Practice / any mode entry). Named `collapseHero` still
   * because every engagement trigger below already calls it; idempotent. */
  function collapseHero(): void {
    setView('practice');
  }
  /** Back to the landing (the navbar wordmark). */
  function showLanding(): void {
    setView('landing');
    window.scrollTo({ top: 0 });
  }

  /**
   * Coherent-IA Task 1 (nav operates the app + scroll/mode-entry fixes):
   * scrolls the practice tool into view — the sticky `.navbar`'s own top by
   * default (used wherever the goal is just "bring the whole tool on
   * screen": the navbar's own Practice/Ear-training click interception
   * below, the "Start practising" CTA, and a plain `?raga=`/`?tanpura=1`
   * load-time deep-link with no more specific surface of its own), or a
   * caller-supplied `target` further down the stage when THAT surface, not
   * just the top of the page, is what the caller wants in view (the practice
   * banner for a drill, the ear-training mount for `enterEarMode`/the
   * `?mode=ear` deep-link).
   *
   * Bugfix (the "Start practising" CTA's old raw `href="#app"` overshoot,
   * landing at ~y624 in the field report instead of at the tool): that bug
   * was a RACE, not a wrong offset — the browser's native anchor-jump (or an
   * equally naive `scrollIntoView` called synchronously right after
   * `collapseHero()`) measures the target's position in the SAME tick the
   * hero's collapse attribute was just set, before the hero's own 0.36s CSS
   * transition has actually run — so it measures the hero at its OLD
   * (expanded) height, then the hero visually shrinks out from under the
   * scroll while it's still animating toward that stale target, landing well
   * past the tool. Every caller of this function may have just called
   * `collapseHero()` moments earlier in the same tick, so THIS function
   * forces that collapse to commit its FINAL layout synchronously before
   * measuring anything: toggle `html.hero-no-transition` (styles.css — turns
   * off the hero's transitions), force one reflow (`offsetHeight`), toggle it
   * back off. That's the standard "resolve a pending transition to its end
   * state right now" trick — harmless (a no-op) when nothing is actually
   * transitioning, which covers every OTHER `collapseHero()` call site (a mic
   * take starting, the tanpura, both of which never scroll afterward, so
   * their collapse still animates normally for whoever's watching).
   *
   * When scrolling to a `target` below the navbar, `scrollMarginTop` is
   * (re)computed from the sticky navbar's OWN current height on every call
   * (it varies by viewport width as the navbar wraps) so the target never
   * lands hidden behind it — the fix `enterPracticeMode` used inline before
   * this helper existed, now shared by every caller. The navbar itself needs
   * no such compensation: it IS the sticky header, so its own top is already
   * exactly where `scrollIntoView` should land it.
   *
   * `scroll-behavior` (smooth vs instant) is CSS-driven, not decided here —
   * see the `prefers-reduced-motion` block at the end of styles.css, the same
   * convention every other `scrollIntoView` call in this file already relies
   * on.
   */
  function scrollToTool(target?: HTMLElement): void {
    // Homepage rework: entering practice view display-hides the hero and shows
    // #app at the top (below the sticky navbar), so there's no collapse-race to
    // resolve any more — just scroll the target (or the top of the tool) into
    // view, offsetting a below-navbar target by the sticky navbar's height.
    const navbarEl = $doc<HTMLElement>('navbar');
    const el = target ?? navbarEl ?? root;
    if (target && navbarEl) {
      target.style.scrollMarginTop = `${navbarEl.getBoundingClientRect().height}px`;
    }
    el.scrollIntoView({ block: 'start' });
  }

  /** Coherent-IA Task 1: the SPA's own handler for the navbar's intercepted
   * "Practice" click (see `navbar.ts`'s `NavbarActions` doc) — exits
   * whichever mode (drill/ear) is active, mirroring the drill's own "Exit
   * practice"/ear training's own "Exit ear training" buttons, then brings
   * the tool back into view. Counts as engaging with the tool, same as every
   * other `collapseHero()` call site. References `exitPracticeMode`/
   * `exitEarMode` (hoisted function declarations defined further down this
   * file) — safe, since this only ever RUNS later, from a real click, well
   * after the whole of `mountApp` has finished setting up. */
  function goToPractice(): void {
    collapseHero();
    const mode = store.get().practiceMode;
    if (mode === 'drill') exitPracticeMode();
    else if (mode === 'ear') exitEarMode();
    scrollToTool();
  }

  /** Coherent-IA Task 1: the SPA's own handler for the navbar's intercepted
   * "Ear training" click — just `enterEarMode()`, the exact same entry point
   * the stage's own "🎧 Ear training" button and the
   * `__SWARASENSE_ENTER_EAR__` harness hook use (also a hoisted function
   * declaration, see `goToPractice`'s own doc for why calling it from here,
   * before its textual definition, is safe). If ear-training mode is
   * already open (e.g. the visitor scrolled away and clicked the nav link to
   * jump back), `enterEarMode()` itself no-ops (it only enters from free
   * practice) — re-scroll to it anyway so the click still does SOMETHING
   * visible instead of nothing. */
  function goToEarTraining(): void {
    // `enterEarMode()` only enters from free practice, so if a drill is active
    // (e.g. launched from the results panel's "Try a warm-up"), exit it first —
    // otherwise the nav click would be a dead no-op mid-drill. (Ear mode already
    // open → enterEarMode no-ops; we re-scroll below so the click still does
    // something visible.)
    if (store.get().practiceMode === 'drill') exitPracticeMode();
    enterEarMode();
    if (store.get().practiceMode === 'ear') scrollToTool(earMountEl);
  }

  // UI reimagine (Task 2): "Practice" is always the active navbar item on
  // the SPA — there is no router (see this function's own doc comment), so
  // this page is always the "Practice" destination. Coherent-IA Task 1: pass
  // `actions` so the SPA (and only the SPA — see `navbar.ts`'s `NavbarActions`
  // doc) intercepts Practice/Ear-training clicks into in-app mode switches.
  // The navbar is the persistent shell in index.html (outside #app), visible on
  // both the landing and the practice view. Guarded for harness/unit pages that
  // mount #app without the shell.
  const navbarShell = $doc<HTMLElement>('navbar');
  if (navbarShell) {
    mountNavbar(navbarShell, 'practice', {
      onPracticeClick: goToPractice,
      onEarTrainingClick: goToEarTraining,
      onHomeClick: showLanding,
    });
  }

  // Connected-Swarasense (W3/W5): the Sa popover is now the "Your voice" panel,
  // so it needs the mic range-finder capture + the raga pool for My ragas.
  // `captureSustainedHz` is a hoisted function declaration defined below.
  const popoverDeps = {
    store,
    engine,
    tanpura,
    metronome,
    captureSustainedHz,
    allRagas: () => allRagaRecords(),
  };
  const controlsRowAnchors = mountControlsRow($('controls-row'), popoverDeps);

  // Connected-Swarasense (W4): the personalized welcome strip — returning
  // visitors get "Welcome back · Sa · sessions · Continue {last raga}" + their
  // ragas + a first-run "set your shruti" nudge; a brand-new visitor gets
  // nothing here (the crawlable hero handles them). Progressive enhancement.
  const homeStripEl = $doc<HTMLElement>('home-strip');
  if (homeStripEl) {
    void mountHomeStrip(homeStripEl, {
      onContinue: (raga, level) => {
        const resolved = resolveRagaParam(raga, allRagaRecords());
        if (resolved) store.set({ ragaName: resolved.name });
        if (level === 'beginner' || level === 'intermediate' || level === 'advanced') {
          store.set({ level });
        }
        collapseHero();
        scrollToTool();
      },
      // First-run nudge → enter practice then open the "Your voice" panel.
      onSetShruti: () => {
        collapseHero();
        controlsRowAnchors.saBtn.click();
      },
    });
  }
  // Never aborted: like the controls row's own internal signal, this popover
  // wiring lives for the whole page's lifetime (no screen to unmount it on).
  // A separate controller from the row's is fine — neither is ever aborted,
  // so there is nothing to keep in sync between them.
  const { signal: popoverSignal } = new AbortController();
  mountPopovers(
    {
      settingsBtn: controlsRowAnchors.settingsBtn,
      settingsPanel: $('settings-popover'),
      saBtn: controlsRowAnchors.saBtn,
      saPanel: $('sa-popover'),
      saLabel: controlsRowAnchors.saLabel,
      droneCaretBtn: $<HTMLButtonElement>('drone-caret'),
      dronePanel: $('drone-popover'),
      thaalamCaretBtn: $<HTMLButtonElement>('thaalam-caret'),
      thaalamPanel: $('thaalam-popover'),
      scrim: $('popover-scrim'),
    },
    popoverDeps,
    popoverSignal,
  );

  const ragaMetaEl = $('raga-meta');
  function updateRagaMeta(name: string): void {
    const record = ragaSeedFor(name);
    ragaMetaEl.innerHTML = record ? stageCaptionHtml(record) : '';
  }
  updateRagaMeta(initial.ragaName);

  const ribbonEl = $('ribbon');
  const ribbon = new Ribbon(ribbonEl);
  let scoring: Scoring = resolveScoring(initial.ragaName);
  ribbon.setScale(scoring.targetCents, scoring.gamakaProfile);

  // Task 2 (targeted drills): the notation lane mounts ABOVE the ribbon (see
  // the markup above) and stays hidden/empty in free practice — only
  // `enterPracticeMode` below shows it and gives it a timeline.
  const notationLane = new NotationLane($('notation-lane-mount'));
  const notationMountEl = $('notation-lane-mount');
  const practiceBannerEl = $('practice-banner');
  const practiceLabelEl = $('practice-label');
  const practicePatternEl = $('practice-pattern');
  const practiceExitBtn = $<HTMLButtonElement>('practice-exit-btn');

  /** Bugfix (drill pattern visibility): the beat-grid notation lane is an
   * abstract "tokens on a timeline" view — clear once you know how to read
   * it, but it doesn't say outright what to SING. This renders a plain,
   * explicit "Sing: G R G P G" line into the practice banner from the same
   * timeline the lane draws, reusing `swaraLabel` (notation-lane.ts) for the
   * same octave-mark convention. Consecutive repeats (e.g. a drill's held
   * final target landing as two same-swara notes) collapse to one token —
   * the SEQUENCE, not the beat count, is what a learner reads off this line;
   * the beat-grid lane right below stays the authoritative follow-along
   * timing. */
  function singPatternSwaras(timeline: NotationTimeline): string[] {
    const labels: string[] = [];
    for (const note of timeline.notes) {
      const label = swaraLabel(note.swara, note.octave);
      if (labels[labels.length - 1] !== label) labels.push(label);
    }
    return labels;
  }

  function renderSingPattern(swaras: readonly string[]): void {
    practicePatternEl.replaceChildren();
    if (swaras.length === 0) {
      practicePatternEl.removeAttribute('aria-label');
      return;
    }
    const lead = document.createElement('span');
    lead.className = 'practice-pattern-lead';
    lead.textContent = 'Sing:';
    const chips = document.createElement('span');
    chips.className = 'practice-pattern-chips';
    // The container's own `aria-label` below is the single accessible
    // source for this line (same role=img-summary/chips-hidden split
    // `notation-lane.ts` uses for its figure + per-note tokens) — without
    // this, assistive tech reading into the chip spans would announce each
    // bare swara letter with no "Sing:" framing.
    chips.setAttribute('aria-hidden', 'true');
    for (const swara of swaras) {
      const chip = document.createElement('span');
      chip.className = 'practice-pattern-chip';
      chip.textContent = swara;
      chips.appendChild(chip);
    }
    practicePatternEl.append(lead, chips);
    practicePatternEl.setAttribute('aria-label', `Sing: ${swaras.join(' ')}`);
  }

  // The seed bundle (per-raga swarasthana cents + gamaka profiles) loads
  // asynchronously, so the `resolveScoring` call above — needed synchronously
  // so the very first pitch sample has something to grade against — runs
  // before the seed index is populated and falls back to plain-JI cents with
  // an EMPTY gamaka profile. Once the bundle resolves, recompute `scoring`
  // (the same module-level binding `onPitch` and the Stop→`analyzeSession`
  // path both read) from whatever ragam is CURRENT at that moment — not
  // `initial.ragaName` — so this still lands correctly even if the user
  // switched ragam before the seed finished loading.
  void loadSeedBundle().then(() => {
    // Task 5 (content SEO pages): `?raga=<name>` deep-link, resolved only
    // once the seed bundle's raga records actually exist to resolve against
    // (`allRagaRecords()` is empty before this). Goes through the exact same
    // `store.set({ ragaName })` path the controls-row combobox uses (see
    // `controls-row.ts`'s `onSelect`) — the `store.subscribe` below reacts to the
    // change and re-resolves scoring + the stage caption itself, so this
    // doesn't need to do that work directly. An absent/unknown `raga` param
    // resolves to `null` and leaves the default (`initial.ragaName`) in
    // place, unchanged.
    const resolved = resolveRagaParam(deepLinkParams.get('raga'), allRagaRecords());
    if (resolved) store.set({ ragaName: resolved.name });

    const ragaName = store.get().ragaName;
    scoring = resolveScoring(ragaName);
    ribbon.setScale(scoring.targetCents, scoring.gamakaProfile);
    updateRagaMeta(ragaName);
    // The rail's very first render (mounted synchronously above, before this
    // promise resolves) necessarily found the seed bundle still empty and
    // showed its "Loading raga…" placeholder — re-render now that
    // `ragaSeedFor` can actually resolve `ragaName`. Needed even when NO
    // `?raga=` deep-link fired (so `store.set` above never ran, and the
    // `store.subscribe` ragaName-changed branch never fires) — the common
    // case of a plain visit practicing the default raga.
    ragaRail.update();

    // Task 6 (ear-training plan): `?mode=ear` deep-link from the
    // /ear-training/ landing page's CTA — enters ear-training mode on load,
    // through the exact same `enterEarMode` the "🎧 Ear training" button and
    // `__SWARASENSE_ENTER_EAR__` harness hook use (both defined further down
    // this function but already hoisted function declarations, so calling
    // one from here is safe). Resolved AFTER the `?raga=` override above so
    // `?raga=<name>&mode=ear` opens straight into ear training on that raga.
    // `enterEarMode` itself no-ops if `ragaSeedFor` can't find a record for
    // the resolved ragaName (nothing to guess at) or if practiceMode somehow
    // isn't 'free' yet — same guards the button's own click goes through, so
    // this never throws or half-enters the mode. It also never touches audio
    // directly: entering the mode only shows the exercise-pick intro, and a
    // round's first tone plays only once the visitor taps "Start" — a real
    // gesture — so this is safe even before the browser has unlocked audio.
    //
    // Coherent-IA Task 1: a plain `?raga=`/`?tanpura=1` deep-link (no
    // `?mode=ear`) still means "bring me to the tool", not "read the
    // marketing" — a visitor followed a raga page's or the tanpura page's
    // own CTA here specifically to reach the practice surface. Collapse the
    // hero and scroll the tool into view on load, same as every other
    // engagement trigger, instead of leaving them scrolled to the top of a
    // still-full hero with the raga/tanpura state silently applied below the
    // fold. `resolved` (computed above) is reused rather than re-reading
    // `?raga=` — it's only truthy when the param actually named a real raga.
    // `?mode=ear` takes priority when present (matches the `&mode=ear`
    // ordering note above) — `enterEarMode()` already collapses the hero and
    // scrolls its own mount into view, so there's nothing left to do here.
    const tanpuraDeepLink = deepLinkParams.get('tanpura');
    if (deepLinkParams.get('mode') === 'ear') {
      enterEarMode();
    } else if (resolved || tanpuraDeepLink === '1' || tanpuraDeepLink === 'true') {
      collapseHero();
      scrollToTool();
    }
  });

  const recEl = $('rec');
  const recTimeEl = $('rec-time');
  const nowEl = $('now');
  const curSwaraEl = $('cur-swara');
  const curVerdictEl = $('cur-verdict');
  const droneBtn = $<HTMLButtonElement>('drone-btn');
  const thaalamBtn = $<HTMLButtonElement>('thaalam-btn');
  // Task 5 polish: also disabled during practice mode (see
  // `enterPracticeMode`) so the Thaalam-options popover — which live-applies
  // thaalam/tempo changes to a running metronome — can't be opened to
  // retune a drill's fixed thaalam/tempo out from under its notation lane.
  const thaalamCaretBtn = $<HTMLButtonElement>('thaalam-caret');
  const listenBtn = $<HTMLButtonElement>('listen-btn');
  const earBtn = $<HTMLButtonElement>('ear-btn');
  const earMountEl = $('ear-training-mount');
  const liveRowEl = $('live-row');
  const resultsEl = $('results');

  // --- react to ragam/Sa changes made anywhere (today: only the top bar) ---
  // Seeded from `store.get()` (post-profile-apply above), not `initial`, so
  // the first real change is compared against what's actually on screen and
  // the profile-applied Sa/level don't count as a "change" to persist back.
  let lastRagaName = store.get().ragaName;
  let lastPitchClass = store.get().pitchClass;
  let lastOctave = store.get().octave;
  let lastLevel = store.get().level;
  store.subscribe((s) => {
    if (s.ragaName !== lastRagaName) {
      lastRagaName = s.ragaName;
      scoring = resolveScoring(s.ragaName);
      ribbon.setScale(scoring.targetCents, scoring.gamakaProfile);
      updateRagaMeta(s.ragaName);
      ragaRail.update();
      // Keep the ragam combobox's displayed text in sync when the raga is
      // changed from OUTSIDE the combobox (e.g. a `?raga=` deep-link like the
      // sarali-varisai guide's `/?raga=Mayamalavagowla`) — otherwise the
      // control keeps showing the old/default name while the ribbon + rail
      // show the new raga. Skip while the field is focused so we never clobber
      // what the user is actively typing (this branch only fires on a real
      // ragaName change, not on keystroke filtering, so that's the only case).
      const ragaInput = document.getElementById('raga-input') as HTMLInputElement | null;
      if (ragaInput && document.activeElement !== ragaInput) ragaInput.value = s.ragaName;
    }
    if (s.pitchClass !== lastPitchClass || s.octave !== lastOctave) {
      lastPitchClass = s.pitchClass;
      lastOctave = s.octave;
      // Phase A: persist the new Sa so it becomes the default next visit —
      // covers every path that changes it (the Sa popover's pitch/octave
      // selects, "Detect from drone"). Light localStorage write, only on an
      // actual change (this branch is change-gated), never throws.
      updateProfile({ sa: { pitchClass: s.pitchClass, octave: s.octave } });
      if (tanpura.running) {
        void (async () => {
          await tanpura.stop();
          await tanpura.start(noteToFrequency(`${s.pitchClass}${s.octave}`), {
            firstString: 'Pa',
            volume: s.droneVolume,
          });
        })();
      }
    }
    if (s.level !== lastLevel) {
      lastLevel = s.level;
      // Phase A: persist the self-reported level (the settings panel's Level
      // select) so it too becomes the default next visit.
      updateProfile({ level: s.level });
    }
  });

  const saHz = (): number => saHzFor(store.get());

  // --- UI reimagine (Task 3): the raga companion side-rail / "About this
  // raga" bottom-sheet — mounted once, for the whole page's lifetime (like
  // every other Task-1 mount in this function), and re-rendered on raga
  // change via `ragaRail.update()` (see the `store.subscribe` block below).
  // `ensureDrone` mirrors `enterEarMode`'s identical lambda below (both mean
  // "make sure the tonal reference is sounding before a tone plays"); it can
  // safely call the hoisted `startTanpura` function declaration here even
  // though that declaration appears later in this file. ---
  const practiceGridEl = $('practice-grid');
  const railEl = $('raga-rail');
  const ragaRail = renderRagaRail(railEl, {
    getRecord: () => ragaSeedFor(store.get().ragaName),
    saHz,
    tonePlayer,
    ensureDrone: async () => {
      if (!tanpura.running) await startTanpura();
    },
  });

  /** Hides the rail (and collapses `.practice-grid` back to a single column)
   * while ear-training/drill mode has replaced the stage's own free-practice
   * surface — those modes have their own focused UI (a swara-ID round, a
   * fixed-tempo follow-along drill) where a raga companion alongside would
   * be a distraction, not a complement. Restored on exit from either. */
  function setRailVisible(visible: boolean): void {
    railEl.hidden = !visible;
    practiceGridEl.classList.toggle('rail-hidden', !visible);
  }

  // --- live pitch grading: reproduces the pre-Phase-4b live screen's onPitch
  // exactly (hysteresis + confidence), just writing into the stage's readout
  // instead of the old dock's. ---
  let recording = false;
  let recStart = 0;
  let recSamples: RecordedSample[] = [];
  let recUrl: string | null = null;
  let recTimer: ReturnType<typeof setInterval> | undefined;
  let lastVoiced: { hz: number; t: number } | null = null;
  let offStreak = 0;
  const recentPitch: RecordedSample[] = []; // trailing window for gamaka-aware smoothing
  // Encouraging-feedback Task 4: the ✨ in-tune moment. `excellentSinceMs`
  // marks when the CURRENT unbroken run of confident, steady (non-gliding)
  // excellent-band frames began; `null` whenever that run is broken (a worse
  // band, a glide, silence, or an unconfident read). `celebratedThisRun`
  // debounces the sparkle to once per run, so a child sustaining a note for
  // 5s gets one ✨, not five.
  let excellentSinceMs: number | null = null;
  let celebratedThisRun = false;

  function setIdleReadout(verdict: string): void {
    nowEl.dataset.state = 'idle';
    curSwaraEl.textContent = '—';
    curVerdictEl.textContent = verdict;
  }

  /**
   * Clears a finished take so the stage can re-arm for the next one: stops
   * any in-flight review playback (so audio never bleeds across takes — the
   * Task-7 fix from the pre-Phase-4b app, reproduced here), empties `#results`
   * back to Task 1's blank state, revokes the previous recording's blob URL,
   * and resets both the local `recSamples` buffer and the store's `report`/
   * `recording`/`recSamples`. Called by the results panel's "New take"
   * button (Task 3) AND at the start of every new take from the transport
   * button below — pressing "Start listening" again without first pressing
   * "New take" must behave identically (no stale results lingering, no old
   * take's audio still playing) rather than only being safe via one path.
   *
   * Also moves focus to the transport's "Start listening" button: when this
   * is called from the results panel's "New take" click handler, that button
   * (still focused) is about to be removed by the `resultsEl.innerHTML = ''`
   * below it, which would otherwise drop focus to `<body>` and strand a
   * keyboard/screen-reader user with no sense of where they landed. Calling
   * it here (rather than only from that one call site) also covers "Start
   * listening" clicked again without "New take" first — already focused, so
   * this is a harmless no-op restatement of the same focus in that case.
   */
  function resetTake(): void {
    stopPlayback();
    resultsEl.innerHTML = '';
    if (recUrl) {
      URL.revokeObjectURL(recUrl);
      recUrl = null;
    }
    recSamples = [];
    store.set({ report: undefined, recording: undefined, recSamples: [] });
    listenBtn.focus();
  }

  function onPitch(sample: PitchSample): void {
    if (recording && sample.pitchHz !== null) {
      recSamples.push({ t: performance.now() - recStart, freqHz: sample.pitchHz });
    }
    if (sample.pitchHz === null) {
      ribbon.push(null);
      lastVoiced = null;
      offStreak = 0;
      recentPitch.length = 0;
      excellentSinceMs = null;
      celebratedThisRun = false;
      setIdleReadout('no input');
      return;
    }
    const base = saHz();
    const nowMs = performance.now();
    const now = nowMs / 1000;
    // Classify a trailing-smoothed pitch so a gamaka oscillation reads as its
    // centre swara; keep ~200ms of history and take the median.
    recentPitch.push({ t: nowMs, freqHz: sample.pitchHz });
    while (recentPitch.length > 0 && nowMs - recentPitch[0]!.t > 200) recentPitch.shift();
    const smoothHz = smoothPitchHz(recentPitch, recentPitch.length - 1, base);
    const grade = gradePitch(smoothHz, base, scoring.targetCents, store.get().level);
    const moving =
      lastVoiced !== null &&
      Math.abs(pitchVelocityCents(lastVoiced.hz, lastVoiced.t, smoothHz, now)) >
        TRANSITION_VELOCITY_CPS;
    lastVoiced = { hz: smoothHz, t: now };

    offStreak = grade.band === 'wrong' && !moving ? offStreak + 1 : 0;
    const committedWrong = offStreak >= OFF_STREAK_TO_WRONG; // hysteresis: sustained, not a gamaka dip
    const band: PitchBand = committedWrong
      ? 'wrong'
      : grade.band === 'wrong'
        ? 'off' // brief dip mid-phrase — don't alarm
        : grade.band;

    const confident = sample.clarity >= CONFIDENCE_THRESHOLD;
    const totalCents = 1200 * Math.log2(sample.pitchHz / base);
    const displayCents = ((totalCents % 1200) + 1200) % 1200;
    ribbon.push({
      tMs: nowMs,
      cents: displayCents,
      band,
      transition: moving,
      confident,
    });

    // Mirrors the pre-Phase-4b live dock's `data-state`: colours the verdict
    // text (never the swara letter, which stays the stage's accent gold) by
    // band, so "in tune"/"nearly there"/etc. carry colour AND words — never
    // colour alone. Encouraging-feedback Task 4: the 'out' state itself no
    // longer maps to a red/alarm colour in CSS (styles.css uses `--away-ink`,
    // the same soft token the ribbon's "away" trace uses) — kept the state
    // NAME unchanged since it's just a styling hook, not user-facing text.
    nowEl.dataset.state = moving
      ? 'near'
      : band === 'excellent' || band === 'good'
        ? 'in'
        : band === 'wrong'
          ? 'out'
          : 'near';
    curSwaraEl.textContent = grade.nearestSwara;
    // Encouraging-feedback Task 4: the child-facing live readout drops the
    // cents number entirely (the ribbon's own vertical position already
    // conveys closeness) and never scolds — "out of the raga" is gone, and a
    // committed-wrong frame gets a soft "—" rather than a verdict word. The
    // underlying `grade`/`band` computation (cents included) is unchanged and
    // still feeds the ribbon + the post-take review, which stay numeric.
    curVerdictEl.textContent = moving
      ? '…'
      : band === 'excellent' || band === 'good'
        ? 'in tune'
        : band === 'wrong'
          ? '—'
          : 'nearly there';

    // ✨ in-tune moment: a steady (non-gliding), confidently-detected
    // excellent-band note sustained for EXCELLENT_SUSTAIN_MS triggers one
    // sparkle near the swara readout, then debounces until the run breaks.
    if (band === 'excellent' && confident && !moving) {
      excellentSinceMs ??= nowMs;
      if (!celebratedThisRun && nowMs - excellentSinceMs >= EXCELLENT_SUSTAIN_MS) {
        celebrate(curSwaraEl);
        celebratedThisRun = true;
      }
    } else {
      excellentSinceMs = null;
      celebratedThisRun = false;
    }
  }

  // --- practice mode (Task 2, non-looping fix): the NotationLane
  // follow-along cursor, driven from the SAME `recStart` timestamp the REC
  // clock above already uses, so "elapsed time since Start" has exactly one
  // source of truth. Final fix (targeted drills, pre-deploy): the cursor
  // advances ONCE through the phrase (0 -> totalBeats) and never wraps —
  // `dtwAlign`/`scoreAlignment` are a single monotonic pass over
  // `timeline.notes`, so looping let a 2nd sung pass pile its frames onto
  // the last note(s) and score correctly-sung notes as wrong, and let the
  // audible metronome (which keeps cycling its own thaalam grid) drift out
  // of sync with a cursor that had wrapped mid-cycle. ---
  let practiceRaf = 0;
  let phraseEndTimer: ReturnType<typeof setTimeout> | undefined;

  /** Brief "you finished!" hold after the cursor reaches the end of the
   * phrase before auto-stopping the take: gives the singer's final held
   * note a moment to actually finish (its tail is still being recorded
   * during this window) and reads as a natural completion rather than an
   * abrupt cutoff exactly on the beat. */
  const PHRASE_END_GRACE_MS = 600;

  /** Stops the follow-along cursor's rAF loop AND cancels any pending
   * phrase-end auto-stop grace timer — the two always share a lifecycle
   * (both belong to "is the cursor still actively tracking a take"), so a
   * manual Stop, exiting practice mode, or the auto-stop path itself all
   * clean up both from one call, and a stray timer can never fire an
   * auto-stop after the take has already ended some other way. */
  function stopPracticeClock(): void {
    if (practiceRaf) cancelAnimationFrame(practiceRaf);
    practiceRaf = 0;
    if (phraseEndTimer !== undefined) {
      clearTimeout(phraseEndTimer);
      phraseEndTimer = undefined;
    }
  }

  function tickPracticeClock(): void {
    const tl = store.get().timeline;
    if (!tl) {
      stopPracticeClock();
      return;
    }
    const beats = elapsedBeats(performance.now() - recStart, tl.tempoBpm);
    if (phraseComplete(beats, tl.totalBeats)) {
      // Land the cursor exactly at the end (held, not mid-note) and stop
      // advancing it — the phrase is over — then auto-stop the take itself
      // after a brief settle so the last note's tail is still captured.
      notationLane.setCursorBeat(tl.totalBeats);
      stopPracticeClock();
      phraseEndTimer = setTimeout(() => {
        phraseEndTimer = undefined;
        void stopTakeAndReview();
      }, PHRASE_END_GRACE_MS);
      return;
    }
    notationLane.setCursorBeat(clampedBeat(beats, tl.totalBeats));
    practiceRaf = requestAnimationFrame(tickPracticeClock);
  }

  /**
   * Enters practice mode with `timeline` loaded: mounts it on the lane above
   * the ribbon and arms the store so the transport's next "Start listening"
   * drives the timeline's own thaalam/tempo (see the `listenBtn` handler
   * below) instead of the user's free-practice thaalam/tempo controls. Task
   * 4 calls this from the child Summary's "Try a warm-up" button (with its
   * own warmer `label`, e.g. "Warm-up: Kalyani — Antara Gandharam"); Task 2's
   * dev trigger (bottom of this function) calls it directly with no `label`,
   * so it falls back to the generic "Practice: {raga} — {swara}" — both
   * exercise the exact same entry path.
   *
   * `targetSwara` (Task 5 polish) is the drill's ACTUAL target — the same
   * value the caller passed `generateDrill` — carried in the store
   * (`drillTargetSwara`) for anything downstream that needs "what is this
   * drill about" (this function's own label fallback, and the gentle
   * review's headline after Stop) instead of guessing it back out of
   * `timeline.notes.at(-1)`.
   */
  function enterPracticeMode(
    timeline: NotationTimeline,
    label?: string,
    targetSwara?: string,
  ): void {
    // UX fix (hero collapse): entering a drill counts as engaging with the
    // tool (see `collapseHero()`'s doc above).
    collapseHero();
    resetTake();
    // Final fix (targeted drills, Minor 1): resolve the label ONCE here and
    // carry it in the store (`drillLabel`) rather than only writing it into
    // the DOM — so `onTryAgain` (below) can re-enter with the SAME warm
    // label instead of falling back to the raw 12-symbol `targetSwara` key.
    const resolvedLabel = label ?? `Practice: ${timeline.raga} — ${targetSwara ?? ''}`;
    store.set({
      practiceMode: 'drill',
      timeline,
      drillTargetSwara: targetSwara,
      drillLabel: resolvedLabel,
    });
    notationLane.setTimeline(timeline);
    setRailVisible(false); // drill mode replaces the stage's own free-practice surface
    notationMountEl.hidden = false;
    practiceBannerEl.hidden = false;
    practiceLabelEl.textContent = resolvedLabel;
    renderSingPattern(singPatternSwaras(timeline));
    // The transport's Thaalam toggle (and its "⋯" options caret, which lets
    // a grown-up retune thaalam/tempo live even while a metronome is
    // running — see settings-popover.ts's `renderThaalamPanel`) are both
    // driven by the drill's own fixed thaalam/tempo while practicing (see
    // `listenBtn`'s Start branch below), not the user's free-practice
    // controls — disabled rather than silently ignored, so neither looks
    // like a live, independent control. Without this, opening the caret and
    // changing tempo/thaalam mid-drill would retune the audible metronome
    // out from under the notation lane's cursor (still clocked from the
    // timeline's own fixed `tempoBpm`), a confusing desync for no benefit
    // (the drill's thaalam is fixed by design).
    thaalamBtn.disabled = true;
    thaalamCaretBtn.disabled = true;
    // Ear-training mode is a `practiceMode` sibling of `'drill'` — the two are
    // mutually exclusive, so the entry into one must not be reachable while
    // the other is active (this mirrors thaalam being disabled above rather
    // than something a drill could retune out from under itself).
    earBtn.disabled = true;
    // Bugfix (drill pattern visibility): `.practice-banner` used to be
    // `position: sticky` with an inline `top` computed here from the bar's
    // current height (Task 5's a11y/responsive fix), pinning it just below
    // the sticky bar for the whole practice session. On mobile the bar
    // wrapped to a taller two-line height than the sticky `top` accounted
    // for at the moment of the FIRST paint, so the sticky banner landed
    // overlapping — not below — the static notation lane right after it in
    // the DOM, burying the pattern the student is meant to sing behind the
    // banner. The banner is now a normal-flow header of the practice
    // section instead (see styles.css): it no longer stays pinned while
    // scrolling down to "Start listening", but the pattern being reliably
    // visible matters far more than that convenience.
    //
    // UI reimagine (Task 2): the ONE element still `position: sticky` at the
    // top of the page is now `.navbar` (the pre-reimagine single `.bar` split
    // into `.navbar` + a non-sticky `.controls-row` beneath it — see
    // `mountApp`'s own doc comment) — so a plain `scrollIntoView` on the
    // now-normal-flow banner would land it at document y=0, right UNDER the
    // sticky navbar, hiding the banner (and its new "Sing: ..." line) behind
    // it on mobile — the exact "hidden behind the top bar" failure this fix
    // removes for the lane, just moved one element over. `scrollMarginTop`,
    // recomputed from the NAVBAR's current height on every entry (it varies
    // by viewport width as the navbar wraps), only changes `scrollIntoView`'s
    // target offset — it does not touch the banner's own position/stacking,
    // so it can't reintroduce the overlap-the-lane bug this fix set out to
    // remove. (The controls row itself is no longer sticky, so its own
    // height doesn't need to be accounted for here — it scrolls away with
    // the rest of the page.)
    //
    // Coherent-IA Task 1: this scroll-margin + scrollIntoView pair moved into
    // the shared `scrollToTool()` helper (also handles a just-collapsed hero
    // whose CSS transition hasn't settled yet — see that function's own doc)
    // — this call site is now just the one line below.
    scrollToTool(practiceBannerEl);
    listenBtn.focus({ preventScroll: true });
  }

  /** Leaves practice mode back to free practice: stops anything still
   * running (mic, metronome, the cursor clock), clears the lane, and resets
   * the take — the same shape "Exit practice" and the review stub's "Done"
   * both need, so both just call this. */
  function exitPracticeMode(): void {
    void (async () => {
      if (engine.running) {
        await engine.stop();
        recording = false;
      }
      if (recTimer !== undefined) {
        clearInterval(recTimer);
        recTimer = undefined;
      }
      recEl.hidden = true;
      if (metronome.running) {
        await metronome.stop();
        thaalamBtn.classList.remove('on');
        thaalamBtn.setAttribute('aria-pressed', 'false');
      }
      stopPracticeClock();
      thaalamBtn.disabled = false;
      thaalamCaretBtn.disabled = false;
      earBtn.disabled = false;
      listenBtn.setAttribute('aria-pressed', 'false');
      listenBtn.textContent = 'Start listening';
      setIdleReadout('ready');
      notationLane.clear();
      setRailVisible(true); // restore the companion rail — back to free practice
      notationMountEl.hidden = true;
      practiceBannerEl.hidden = true;
      // Undo enterPracticeMode's inline `scrollMarginTop` so a hidden (but
      // still-in-DOM) banner doesn't carry a stale bar-height offset into
      // whatever renders next.
      practiceBannerEl.style.scrollMarginTop = '';
      renderSingPattern([]);
      store.set({
        practiceMode: 'free',
        timeline: undefined,
        drillTargetSwara: undefined,
        drillLabel: undefined,
      });
      resetTake();
    })();
  }

  practiceExitBtn.addEventListener('click', exitPracticeMode);

  // --- ear-training mode (ear-training plan, Task 3): a self-contained
  // swara-ID round (`ear-training.ts`) mounted in place of the stage's
  // pitch-grading surface — no mic, no metronome, just "listen, then tap". ---

  /** Set (while a listen is in flight) to a function that ends that listen
   * immediately, reading it as "not-yet" and stopping the engine — the
   * `cancelSingBack` capability `ear-training.ts` calls whenever it's about
   * to redraw or leave the current question, so a "sing it" take never
   * outlives the screen it belongs to. `undefined` whenever no sing-back
   * listen is currently open (the common case), so calling it is always
   * safe.
   *
   * Returns a promise that resolves only once `engine.stop()` has actually
   * completed (see `finish()` below) — `ear-training.ts`'s `exit()` awaits
   * it before calling `onExit()`, so a cancelled-mid-teardown sing-back can
   * never hand control back to free practice's "Start listening" while
   * `engine.running` still reads true. */
  let cancelActiveSingBack: (() => Promise<void>) | undefined;

  /**
   * Ear-training Task 5: the swara-ID exercise's OPTIONAL "sing it"
   * sing-back. A short (`SING_BACK_LISTEN_MS`) mic take through the exact
   * same `AudioEngine` (and so the exact same `window.__SWARASENSE_TEST__`/
   * `__feedPitch` hook) a normal free-practice take already uses — this is
   * one extra, much shorter LISTEN on that one pipeline, not a second
   * pitch-detection system. Grades every frame with the SAME
   * `gradePitch`/`saHzFor` pair every other grading call site in this file
   * reads, just against a single-entry `{ sing: targetCents }` map (rather
   * than the whole raga's ladder) — the question is only ever "did this
   * land on THIS one swara", not "which of the 12 is this closest to".
   *
   * Resolves `'landed'` the moment a confident excellent/good-band read
   * holds for `SING_BACK_SUSTAIN_MS` (a quick confirmation is friendlier
   * here than making a child hold a note the whole listen through),
   * `'not-yet'` if the listen times out without one, and `'unavailable'`
   * without ever prompting for the mic a second time when a take is
   * somehow already running (defensive — `enterEarMode` only being
   * reachable with the engine idle already rules this out in practice) or
   * `getUserMedia`/`AudioEngine.start` itself rejects (denied/unsupported
   * mic) — the sing-back affordance is optional by design, so a denied mic
   * degrades it to unavailable rather than surfacing a browser permission
   * error into this gentle, number-free flow.
   *
   * Review fix: every OTHER engine-teardown path in this file
   * (`exitPracticeMode`, `stopTakeAndReview`) `await`s `engine.stop()`
   * before letting the UI reach for a new `engine.start()`. `finish()`
   * below does the same — it awaits the stop BEFORE resolving/settling —
   * so `engine.running` (`AudioEngine`'s `ctx !== undefined`) is guaranteed
   * false by the time either this promise's resolution or a `cancelSingBack`
   * caller gets to run their own next step. Without this, a fast "Sing it"
   * → "Exit ear training" → "Start listening" (or a rapid "Sing it again")
   * could land inside the gap between `engine.stop()` being KICKED OFF and
   * its `await ctx.close()` actually finishing, during which
   * `engine.start()` silently no-ops (`if (this.running) return;`) into a
   * phantom, pitch-less recording rather than throwing.
   */
  async function listenForSwara(targetCents: number): Promise<SingBackResult> {
    if (engine.running) return 'unavailable';
    await cancelActiveSingBack?.(); // belt-and-braces: at most one listen open at a time, fully torn down first
    const level = store.get().level;
    const base = saHz();
    const targetMap: Record<string, number> = { sing: targetCents };
    const recent: { t: number; freqHz: number }[] = [];
    let landedSinceMs: number | null = null;

    return new Promise<SingBackResult>((resolve) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const finish = async (result: SingBackResult): Promise<void> => {
        if (settled) return;
        settled = true;
        cancelActiveSingBack = undefined;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        await engine.stop();
        resolve(result);
      };
      cancelActiveSingBack = () => finish('not-yet');

      const handlePitch = (sample: PitchSample): void => {
        if (settled) return;
        if (sample.pitchHz === null || sample.clarity < CONFIDENCE_THRESHOLD) {
          landedSinceMs = null;
          recent.length = 0;
          return;
        }
        const nowMs = performance.now();
        recent.push({ t: nowMs, freqHz: sample.pitchHz });
        while (recent.length > 0 && nowMs - recent[0]!.t > 200) recent.shift();
        const smoothHz = smoothPitchHz(recent, recent.length - 1, base);
        const grade = gradePitch(smoothHz, base, targetMap, level);
        if (grade.band === 'excellent' || grade.band === 'good') {
          if (landedSinceMs === null) landedSinceMs = nowMs;
          else if (nowMs - landedSinceMs >= SING_BACK_SUSTAIN_MS) void finish('landed');
        } else {
          landedSinceMs = null;
        }
      };

      void engine
        .start(getProfile(store.get().instrument), handlePitch, {})
        .then(async () => {
          if (settled) {
            // Cancelled while `start()` was still in flight — `finish()`'s
            // own `engine.stop()` may have run before `ctx`/`node`/`source`
            // were even assigned (a no-op then), so stop again now that
            // they actually exist.
            await engine.stop();
            return;
          }
          timeoutId = setTimeout(() => void finish('not-yet'), SING_BACK_LISTEN_MS);
        })
        .catch(() => finish('unavailable'));
    });
  }

  /**
   * Hides the stage's ribbon + live-row (which also hides the transport's
   * own Drone/Thaalam/Start-listening controls and this very button — the
   * round's own rendered screens carry their own "Exit ear training"/"Done"
   * instead, see `ear-training.ts`) and hands `#ear-training-mount` to
   * `renderEarTraining`. Guarded to only run from free practice with a
   * loaded raga — `earBtn` is already disabled whenever `practiceMode !==
   * 'free'` or a take is running (see the disable/enable pairs alongside
   * `enterPracticeMode`/`exitPracticeMode`/`stopTakeAndReview` above), so
   * this is a defensive belt-and-braces check, not the primary guard; the
   * seed-bundle check guards the brief window before `loadSeedBundle()`
   * resolves, where `ragaSeedFor` has nothing to return yet.
   */
  /** Whether the tanpura was already playing when the user entered ear
   * training. If it was NOT (ear training started it as the exercise's tonal
   * reference), `exitEarMode` stops it again so leaving the mode leaves the
   * drone as it found it; if the user already had it on in free practice, it
   * stays on. */
  let droneOnBeforeEar = false;

  function enterEarMode(): void {
    const state = store.get();
    if (state.practiceMode !== 'free') return;
    const record = ragaSeedFor(state.ragaName);
    if (!record) return;
    droneOnBeforeEar = tanpura.running;
    // UX fix (hero collapse): entering ear training counts as engaging with
    // the tool (see `collapseHero()`'s doc above).
    collapseHero();
    store.set({ practiceMode: 'ear' });
    setRailVisible(false); // ear-training mode replaces the stage's own free-practice surface
    ribbonEl.hidden = true;
    liveRowEl.hidden = true;
    earMountEl.hidden = false;
    renderEarTraining(earMountEl, {
      raga: record,
      tonePlayer,
      saHz,
      // Mirrors the transport's own Drone toggle (`startTanpura` below) so a
      // round always has the tanpura's tonal reference before its first
      // tone plays, without restarting an already-running drone.
      ensureDrone: async () => {
        if (!tanpura.running) await startTanpura();
      },
      onExit: exitEarMode,
      listenForSwara,
      cancelSingBack: () => cancelActiveSingBack?.() ?? Promise.resolve(),
      // Phase A: record a lighter 'ear' history record each time a round
      // completes (no per-swara metrics — an ear round produces none). Uses
      // the raga/level current at completion time. Fire-and-forget, guarded.
      onRoundComplete: () => {
        const s = store.get();
        void recordSession({
          dateISO: new Date().toISOString().slice(0, 10),
          raga: s.ragaName,
          level: String(s.level),
          mode: 'ear',
        }).catch(() => {});
      },
    });
    // Coherent-IA Task 1: bring the ear-training mount into view — the bug
    // this fixes is the navbar's "Ear training" link (and the `?mode=ear`
    // deep-link, both of which call this same function) opening the mode
    // BELOW the hero/navbar, out of the fold, looking like nothing happened.
    scrollToTool(earMountEl);
  }

  /** Leaves ear-training mode back to free practice: stops any ringing tone,
   * clears the mount, and restores the ribbon/live-row — the symmetric
   * counterpart to `enterEarMode`, and (like `exitPracticeMode`) the one path
   * every exit affordance in `ear-training.ts` (mid-round "Exit ear
   * training", the summary's "Done") calls. */
  function exitEarMode(): void {
    tonePlayer.stop();
    // Stop the drone iff ear training was the one that started it — restoring
    // the tanpura to the state it was in before the user entered the mode.
    if (!droneOnBeforeEar && tanpura.running) void stopTanpura();
    store.set({ practiceMode: 'free' });
    setRailVisible(true); // restore the companion rail — back to free practice
    earMountEl.hidden = true;
    earMountEl.innerHTML = '';
    // Undo `enterEarMode`'s `scrollToTool`-applied `scrollMarginTop` so a
    // hidden (but still-in-DOM) mount doesn't carry a stale navbar-height
    // offset into whatever renders next — mirrors `exitPracticeMode`'s
    // identical reset of `practiceBannerEl` just below.
    earMountEl.style.scrollMarginTop = '';
    ribbonEl.hidden = false;
    liveRowEl.hidden = false;
  }

  earBtn.addEventListener('click', enterEarMode);


  /**
   * Phase A voice-range finder capture: run ONE mic listen and resolve a
   * single sustained pitch in Hz — the median of a steady held window — or
   * `null` if a take is already running, the mic is denied/unsupported, or no
   * steady note was heard within `RANGE_CAPTURE_LISTEN_MS`. Modeled on
   * `listenForSwara` (same engine + `cancelActiveSingBack` teardown + test
   * hook), but instead of grading against a target it just waits for the
   * singer to hold ANY note steady, then reports its pitch. Never throws —
   * the You view degrades gracefully to "couldn't hear a steady note" / manual
   * Sa on `null`.
   */
  async function captureSustainedHz(onHeard?: (hz: number | null) => void): Promise<number | null> {
    if (engine.running) return null;
    await cancelActiveSingBack?.();
    // Every voiced frame heard this listen (with its timestamp). We resolve
    // early once a trailing `RANGE_CAPTURE_HOLD_MS` window is a steady cluster,
    // and fall back to the median of ALL voiced frames on timeout so a real
    // sung note is never rejected just because it wobbled.
    const voiced: { t: number; hz: number }[] = [];
    const median = (samples: { hz: number }[]): number => {
      const hzs = samples.map((s) => s.hz).sort((a, b) => a - b);
      return hzs[Math.floor(hzs.length / 2)]!;
    };
    return new Promise<number | null>((resolve) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const finish = async (hz: number | null): Promise<void> => {
        if (settled) return;
        settled = true;
        cancelActiveSingBack = undefined;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        await engine.stop();
        resolve(hz);
      };
      cancelActiveSingBack = () => finish(null);

      const handlePitch = (sample: PitchSample): void => {
        if (settled) return;
        const nowMs = performance.now();
        if (sample.pitchHz === null || sample.clarity < RANGE_CAPTURE_MIN_CLARITY) {
          onHeard?.(null); // unvoiced/too quiet — tell the UI, but DON'T discard
          return; //          the run: real notes have brief low-clarity frames.
        }
        const hz = sample.pitchHz;
        voiced.push({ t: nowMs, hz });
        onHeard?.(hz);
        // Trailing window of the last HOLD_MS. Resolve once it spans most of
        // that window AND most of its frames sit within STABLE_CENTS of the
        // window median (tolerant to the odd outlier frame — a single bad
        // frame no longer wipes the whole hold, which is what made the first
        // cut time out on real mics).
        const win = voiced.filter((s) => s.t >= nowMs - RANGE_CAPTURE_HOLD_MS);
        if (
          win.length >= RANGE_CAPTURE_MIN_SAMPLES &&
          nowMs - win[0]!.t >= RANGE_CAPTURE_HOLD_MS * 0.75
        ) {
          const med = median(win);
          const inTol = win.filter(
            (s) => Math.abs(1200 * Math.log2(s.hz / med)) <= RANGE_CAPTURE_STABLE_CENTS,
          ).length;
          if (inTol / win.length >= 0.6) void finish(med);
        }
      };

      void engine
        // Match free practice's input-gain boost (`s.micGain`) — without it the
        // raw signal often stays under the detector's clarity bar, so nothing
        // registers as voiced and the capture times out (the live-mic bug).
        .start(getProfile(store.get().instrument), handlePitch, { inputGain: store.get().micGain })
        .then(async () => {
          if (settled) {
            await engine.stop();
            return;
          }
          timeoutId = setTimeout(() => {
            // Didn't hold perfectly steady, but if we heard a real note at all,
            // use the median of everything heard rather than failing outright.
            void finish(voiced.length >= RANGE_CAPTURE_MIN_SAMPLES ? median(voiced) : null);
          }, RANGE_CAPTURE_LISTEN_MS);
        })
        .catch(() => finish(null));
    });
  }


  // --- Stop (manual OR the practice clock's phrase-end auto-stop) share this
  // ONE path, so a drill's take always ends the same way no matter which
  // triggered it. `stopInFlight` is a synchronous re-entrancy guard — set
  // TRUE before the first `await` below — so a manual Stop click racing the
  // auto-stop grace timer (or either firing twice) can never run the
  // engine-stop/score/render sequence more than once for the same take. ---
  let stopInFlight = false;

  async function stopTakeAndReview(): Promise<void> {
    if (stopInFlight || !engine.running) return;
    stopInFlight = true;
    try {
      await engine.stop();
      recording = false;
      if (recTimer !== undefined) {
        clearInterval(recTimer);
        recTimer = undefined;
      }
      recEl.hidden = true;
      listenBtn.setAttribute('aria-pressed', 'false');
      listenBtn.textContent = 'Start listening';
      setIdleReadout('ready');

      const state = store.get();
      // Re-enable "Ear training" once a take actually finishes — EXCEPT
      // mid-drill (`state.practiceMode` still reads `'drill'` here; only
      // `exitPracticeMode`, triggered later by the gentle review's own "Try
      // again"/"Done", re-enables it there), so a learner reviewing a drill
      // take can't jump into ear-training mode out from under it.
      if (state.practiceMode !== 'drill') earBtn.disabled = false;
      // Practice mode drives its own metronome (the drill's thaalam/tempo,
      // started in the Start branch below) — stop it and the follow-along
      // cursor clock here, symmetric with that start. `stopPracticeClock`
      // also cancels any still-pending phrase-end auto-stop timer, so this
      // is safe to call even when THIS call IS that auto-stop firing.
      if (state.practiceMode === 'drill') {
        stopPracticeClock();
        if (metronome.running) {
          await metronome.stop();
          thaalamBtn.classList.remove('on');
          thaalamBtn.setAttribute('aria-pressed', 'false');
        }
      }

      const rec = engine.takeRecording();
      let sessionRecording: SessionRecording | undefined;
      if (rec) {
        sessionRecording = { url: URL.createObjectURL(rec.blob), mimeType: rec.mimeType };
        recUrl = sessionRecording.url;
      }
      const takeSaHz = saHz();
      const report = analyzeSession(
        recSamples,
        takeSaHz,
        scoring.targetCents,
        scoring.gamakaProfile,
        state.level,
      );
      const takeSamples = [...recSamples];
      store.set({ report, recording: sessionRecording, recSamples: takeSamples });

      // Phase A: append this completed take to the on-device practice history
      // (fire-and-forget, guarded — `recordSession` never throws, but the
      // extra `.catch` makes the intent explicit and belt-and-braces against
      // a rejected promise ever surfacing as an unhandled rejection). One
      // record per Stop, before the drill branch's early return below, so a
      // drill take is captured too (mode 'drill' vs free-practice 'free').
      // `sessionFromReport` reads `durationSec` from the report itself.
      void recordSession(
        sessionFromReport(report, {
          raga: state.ragaName,
          level: String(state.level),
          mode: state.practiceMode === 'drill' ? 'drill' : 'free',
          dateISO: new Date().toISOString().slice(0, 10),
        }),
      ).catch(() => {});

      // Practice mode hands off to Task 3's gentle per-note review instead
      // of the free-practice panel — same `scoring` (targetCents/
      // gamakaProfile) and `state.level` every other grading call site in
      // this file already reads.
      if (state.practiceMode === 'drill' && state.timeline) {
        const timeline = state.timeline;
        // Fallback only guards a caller that (like Task 2's dev hook used
        // to) enters practice mode without a target — every real entry
        // path now sets `drillTargetSwara` (see `enterPracticeMode`).
        const targetSwara = state.drillTargetSwara ?? timeline.notes.at(-1)?.swara ?? '';
        renderPracticeReview(resultsEl, {
          timeline,
          recSamples: takeSamples,
          saHz: takeSaHz,
          level: state.level,
          gamakaProfile: scoring.gamakaProfile,
          targetSwara,
          // Minor fix: reuse the SAME warm label the current entry showed
          // (carried in `state.drillLabel`, set by `enterPracticeMode`)
          // instead of passing no label — a bare re-entry would otherwise
          // fall back to `enterPracticeMode`'s generic "Practice: {raga} —
          // {swara}" wording (or worse, previously showed the raw 12-symbol
          // swara key) rather than "Try again" picking up right where this
          // take's own banner left off.
          onTryAgain: () => enterPracticeMode(timeline, state.drillLabel, targetSwara),
          onDone: exitPracticeMode,
        });
        return;
      }

      renderResultsPanel(resultsEl, {
        report,
        recSamples: takeSamples,
        saHz: takeSaHz,
        targetCents: scoring.targetCents,
        gamakaProfile: scoring.gamakaProfile,
        recording: sessionRecording,
        ragaName: state.ragaName,
        level: state.level,
        onNewTake: resetTake,
        onTryDrill: (targetSwara) => {
          // Same generateDrill + enterPracticeMode path as Task 2's
          // `__SWARASENSE_ENTER_DRILL__` dev hook below — a gentle fixed
          // 60 BPM on the raga's OWN thaalam, so this "warm-up" truly is
          // gentler than diving into full free practice.
          //
          // Minor fix: `targetSwara` was closed over at RENDER time, and
          // this results panel can still be sitting on screen (stale) after
          // the user switches the top-bar ragam via the live store — so
          // re-read raga/thaalam from `store.get()` HERE, at click time, not
          // from the `state` this whole `stopTakeAndReview` call captured
          // when the take ended. Re-check the target swara against THAT
          // live raga's own `swara_variants` (mirroring results-panel.ts's
          // render-time guard) before `generateDrill` — `!record` alone
          // isn't enough, since a different valid record missing
          // `targetSwara` would otherwise reach `generateDrill` and throw.
          // No-op (rather than throw or launch a mismatched drill) on a
          // stale click.
          const liveState = store.get();
          const record = ragaSeedFor(liveState.ragaName);
          if (!record || !(targetSwara in record.swara_variants)) return;
          const timeline = generateDrill(record, targetSwara, liveState.thaalamName, 60);
          const swaraName = SWARA_NAMES[targetSwara as SwaraSymbol] ?? targetSwara;
          enterPracticeMode(timeline, `Warm-up: ${timeline.raga} — ${swaraName}`, targetSwara);
        },
      });
    } finally {
      stopInFlight = false;
    }
  }

  /**
   * Task 2 dev/test trigger only (see the `declare global` note near the top
   * of this file). Generates a level-1 anchor+approach drill on the CURRENT
   * raga's first focus swara — its second scale symbol (`syms[1]`, the
   * swara right after Sa), or `targetSwara` if a caller passes one — and
   * enters practice mode with it. A gentle fixed 60 BPM, independent of the
   * user's own tempo setting, matches `generateDrill`'s own default and
   * keeps this trigger's output deterministic for the screenshot harness.
   */
  if (typeof window !== 'undefined' && window.__SWARASENSE_TEST__) {
    window.__SWARASENSE_ENTER_DRILL__ = (targetSwara?: string): void => {
      const s = store.get();
      const record = ragaSeedFor(s.ragaName);
      if (!record) return;
      const syms = Object.keys(record.swara_variants);
      const target = targetSwara ?? syms[1] ?? syms[0];
      if (!target) return;
      const timeline = generateDrill(record, target, s.thaalamName, 60);
      enterPracticeMode(timeline, undefined, target);
    };
    // Ear-training plan Task 3's own dev/test trigger (see the `declare
    // global` note near the top of this file) — just calls the same
    // `enterEarMode` the real "🎧 Ear training" button uses, so the harness
    // exercises the exact production entry path rather than a parallel one.
    window.__SWARASENSE_ENTER_EAR__ = enterEarMode;
  }

  // --- transport: one button that listens, records, and (on stop) reviews ---
  listenBtn.addEventListener('click', () => {
    void (async () => {
      if (engine.running) {
        await stopTakeAndReview();
        return;
      }
      try {
        const s = store.get();
        const startOpts =
          s.droneMode === 'external'
            ? {
                inputGain: s.micGain,
                notchFrequencies: combNotchFrequencies(saHz(), 8000, { firstString: 'Pa' }),
                record: true,
              }
            : { inputGain: s.micGain, record: true };
        // A fresh take must not inherit the previous one's results panel,
        // playback, or recording URL — same reset the results panel's own
        // "New take" button runs (see `resetTake`'s doc above).
        resetTake();
        recStart = performance.now();
        recording = true;
        ribbon.clear(); // fresh take: don't let a previous session's trace bleed in
        excellentSinceMs = null; // fresh take: don't inherit the previous one's ✨ debounce
        celebratedThisRun = false;
        await engine.start(getProfile(s.instrument), onPitch, startOpts);
        // UX fix (hero collapse): mic actually started — this counts as
        // engaging with the tool (see `collapseHero()`'s doc above).
        collapseHero();
        // Practice mode: the metronome + follow-along cursor start together
        // with the mic, driven by the DRILL's own thaalam/tempo (not the
        // user's free-practice controls) — started only after `engine.start`
        // succeeds, so a denied mic permission never leaves an orphaned
        // metronome running with nothing being recorded.
        if (s.practiceMode === 'drill' && s.timeline) {
          const tl = s.timeline;
          notationLane.setCursorBeat(0);
          await metronome.start(resolveThaalamOrAdi(tl.thaalam), tl.tempoBpm, {}, s.thaalamVolume);
          thaalamBtn.classList.add('on');
          thaalamBtn.setAttribute('aria-pressed', 'true');
          practiceRaf = requestAnimationFrame(tickPracticeClock);
        }
        listenBtn.setAttribute('aria-pressed', 'true');
        listenBtn.textContent = 'Stop & review';
        // A mic take (free practice or drill) and an ear-training round are
        // mutually exclusive activities on the same stage — see
        // `stopTakeAndReview`'s matching re-enable.
        earBtn.disabled = true;
        recEl.hidden = false;
        recTimeEl.textContent = '0:00';
        recTimer = setInterval(() => {
          recTimeEl.textContent = mmss((performance.now() - recStart) / 1000);
        }, 1000);
      } catch (e) {
        recording = false;
        setIdleReadout(`mic error: ${(e as Error).message}`);
      }
    })();
  });

  /** Starts the drone and syncs `#drone-btn`'s `.on`/`aria-pressed` state to
   * match — the one path that actually starts the tanpura, shared by the
   * button's own click handler and the `?tanpura=1` deep-link below (Task 5,
   * content SEO pages) so both ever only start it the SAME way. */
  async function startTanpura(): Promise<void> {
    await tanpura.start(saHz(), { firstString: 'Pa', volume: store.get().droneVolume });
    droneBtn.classList.add('on');
    droneBtn.setAttribute('aria-pressed', 'true');
    // UX fix (hero collapse): the tanpura actually starting counts as
    // engaging with the tool (see `collapseHero()`'s doc above) — covers
    // both the button's own click and the `?tanpura=1` deep-link below.
    collapseHero();
  }

  /** Stops the drone and syncs `#drone-btn` back to its off state — the
   * `startTanpura` counterpart. */
  async function stopTanpura(): Promise<void> {
    await tanpura.stop();
    droneBtn.classList.remove('on');
    droneBtn.setAttribute('aria-pressed', 'false');
  }

  droneBtn.addEventListener('click', () => {
    void (tanpura.running ? stopTanpura() : startTanpura());
  });

  // Task 5 (content SEO pages): `?tanpura=1` deep-link from the /tanpura/
  // page and the find-your-shruti guide's CTA — starts the drone on load
  // through the exact same `startTanpura` the button itself uses, so the
  // button's `.on`/`aria-pressed` state is never out of sync with reality.
  // Browsers commonly block an `AudioContext` from actually producing sound
  // until the page has had a user gesture, so `tanpura.start()` (inside
  // `startTanpura`) can reject here even though it would succeed a moment
  // later after a click/keypress — rather than letting that rejection go
  // unhandled (or throwing out of init), arm the same start to retry once on
  // the visitor's first interaction with the page, and give up quietly if
  // even that fails (e.g. denied autoplay policy with no recovery).
  const tanpuraParam = deepLinkParams.get('tanpura');
  if (tanpuraParam === '1' || tanpuraParam === 'true') {
    void startTanpura().catch(() => {
      // If the visitor's first gesture after the block lands on the drone
      // button itself, let the button's OWN click handler above be the one
      // that calls `startTanpura` — otherwise this handler's `startTanpura`
      // (fired on `pointerdown`, which precedes `click`) and the button's
      // click handler race: the click handler can see `tanpura.running` flip
      // to true mid-flight and call `stopTanpura` instead, so the visitor's
      // click appears to do nothing. Skipping the arm here for a
      // drone-button-targeted gesture avoids the double-fire entirely; any
      // OTHER first gesture still starts it directly, same as before.
      const armOnFirstGesture = (e: Event): void => {
        if (e.target instanceof Element && e.target.closest('#drone-btn')) return;
        void startTanpura().catch(() => {
          /* still blocked — nothing more to try; the button itself still works. */
        });
      };
      document.addEventListener('pointerdown', armOnFirstGesture, { once: true });
      document.addEventListener('keydown', armOnFirstGesture, { once: true });
    });
  }

  thaalamBtn.addEventListener('click', () => {
    void (async () => {
      if (metronome.running) {
        await metronome.stop();
        thaalamBtn.classList.remove('on');
        thaalamBtn.setAttribute('aria-pressed', 'false');
      } else {
        const s = store.get();
        await metronome.start(getThaalam(s.thaalamName)!, s.tempo, {}, s.thaalamVolume);
        thaalamBtn.classList.add('on');
        thaalamBtn.setAttribute('aria-pressed', 'true');
      }
    })();
  });

  // The hero itself is static HTML in index.html, OUTSIDE `root` (see
  // index.html's own comment) — queried off `document`, not `$`/`root`.
  // Guarded rather than assumed-present: this same `mountApp` also runs in
  // test/harness pages that mount `#app` without the static hero markup
  // (e.g. store.test.ts-style unit setups), and a missing cosmetic hero must
  // never throw and abort the rest of the app's wiring.
  const heroCta = document.querySelector<HTMLAnchorElement>('.hero-cta');
  if (heroCta) {
    // Coherent-IA Task 1 bugfix: this USED to rely on the plain
    // `href="#app"` native anchor jump (plus `html { scroll-behavior:
    // smooth }`) and only added the focus polish on top. That native jump
    // raced `collapseHero()` — it measured `#app`'s position in the same
    // tick the hero's collapse was kicked off, before the hero's own 0.36s
    // transition had actually run, so it scrolled toward where `#app` WAS
    // (behind the still-full-height hero) and the hero shrank out from under
    // it mid-scroll, landing well past the tool (~y624 in the field report)
    // instead of at it. Now: `preventDefault()` the native jump entirely and
    // do it ourselves through `scrollToTool()`, which forces the hero's
    // collapse to commit its FINAL layout before measuring anything (see
    // that function's own doc) — no race, so it lands correctly on the
    // first try. `href="#app"` stays on the element itself (untouched) as
    // the no-JS fallback (imperfect without this fix, but still a
    // functional jump into the tool, not a dead link).
    heroCta.addEventListener('click', (e) => {
      // Let modifier/middle clicks open in a new tab natively (the `#app`
      // fragment still works there); only intercept a plain left click.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      collapseHero();
      scrollToTool();
      // Unchanged from before this fix: once the scroll settles, move focus
      // into the ragam combobox so a keyboard user who activated the CTA
      // lands somewhere useful instead of with nothing focused.
      const focusRagaInput = () => $<HTMLInputElement>('raga-input').focus({ preventScroll: true });
      const supportsScrollEnd = 'onscrollend' in window;
      if (supportsScrollEnd) {
        window.addEventListener('scrollend', focusRagaInput, { once: true });
      } else {
        // Safari (no `scrollend` as of writing): a fixed delay standing in
        // for "the smooth scroll has probably settled" — the target focus
        // ring is a nice-to-have, not correctness-critical, so an
        // approximate timing is fine.
        window.setTimeout(focusRagaInput, 500);
      }
    });
  }
}
